/**
 * GoogleSetupGuide — v2.5.927
 *
 * Reusable dialog that walks admins through connecting Google Calendar /
 * Drive / Gmail. Use anywhere the app shows "Google Credentials Not
 * configured" so users get a clear path instead of a dead-end message.
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <Button onClick={() => setOpen(true)}>How to configure</Button>
 *   <GoogleSetupGuide open={open} onClose={() => setOpen(false)} product="calendar" />
 */
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { ExternalLink, Copy, Check, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const PRODUCT_LABELS = {
  calendar: { label: 'Google Calendar', apis: ['Google Calendar API'] },
  drive:    { label: 'Google Drive',    apis: ['Google Drive API'] },
  gmail:    { label: 'Gmail',           apis: ['Gmail API'] },
  all:      { label: 'Google Workspace', apis: ['Google Calendar API', 'Google Drive API', 'Gmail API'] },
};

export default function GoogleSetupGuide({ open, onClose, product = 'calendar' }) {
  const info = PRODUCT_LABELS[product] || PRODUCT_LABELS.all;
  const [step, setStep] = useState(0);
  const redirectUri = `${window.location.origin}/api/google/oauth/callback`;
  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`));
  };

  const STEPS = [
    {
      title: 'Open Google Cloud Console',
      body: (
        <>
          <p className="text-sm text-gray-700 mb-3">
            Go to the Google Cloud Console. If you don't have a project yet, create one — it's free.
          </p>
          <Button asChild variant="outline" className="gap-2">
            <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer">
              Open Google Cloud Console <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </Button>
        </>
      ),
    },
    {
      title: `Enable the ${info.label} API`,
      body: (
        <>
          <p className="text-sm text-gray-700 mb-3">
            In your Cloud project, open <b>APIs & Services → Library</b> and enable the following:
          </p>
          <ul className="text-sm text-gray-800 space-y-1 mb-3 list-disc pl-5">
            {info.apis.map(a => <li key={a}><b>{a}</b></li>)}
          </ul>
          <Button asChild variant="outline" className="gap-2">
            <a href="https://console.cloud.google.com/apis/library" target="_blank" rel="noopener noreferrer">
              Open API Library <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </Button>
        </>
      ),
    },
    {
      title: 'Configure OAuth consent screen',
      body: (
        <>
          <p className="text-sm text-gray-700 mb-3">
            In <b>APIs & Services → OAuth consent screen</b>, choose <b>External</b> user type
            and fill in:
          </p>
          <ul className="text-sm text-gray-800 space-y-1 mb-3 list-disc pl-5">
            <li>App name: <b>Konnect</b> (or your brand)</li>
            <li>User support email: your admin email</li>
            <li>Developer contact: your admin email</li>
            <li>Scopes: add these when prompted — <span className="font-mono text-xs bg-gray-100 px-1 py-0.5 rounded">.../auth/calendar</span>, <span className="font-mono text-xs bg-gray-100 px-1 py-0.5 rounded">.../auth/drive.file</span></li>
          </ul>
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            ℹ️ While in "Testing" mode you can only sign in with test users you add explicitly.
            Add your team emails under <b>Test users</b>. Move to "Production" mode when ready.
          </p>
        </>
      ),
    },
    {
      title: 'Create OAuth client credentials',
      body: (
        <>
          <p className="text-sm text-gray-700 mb-3">
            In <b>APIs & Services → Credentials → Create credentials → OAuth client ID</b>:
          </p>
          <ul className="text-sm text-gray-800 space-y-1 mb-3 list-disc pl-5">
            <li>Application type: <b>Web application</b></li>
            <li>Name: <b>Konnect Backend</b></li>
            <li>Authorised redirect URIs → click <b>Add URI</b> and paste the value below:</li>
          </ul>
          <div className="flex items-center gap-2 bg-gray-50 border rounded p-2 mb-3">
            <code className="text-xs flex-1 break-all">{redirectUri}</code>
            <Button size="sm" variant="ghost" onClick={() => copy(redirectUri, 'Redirect URI')} className="h-8">
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
          <p className="text-sm text-gray-700">
            Click <b>Create</b>. Google will show your <b>Client ID</b> and <b>Client Secret</b> —
            copy both, you'll need them in the next step.
          </p>
        </>
      ),
    },
    {
      title: 'Paste the credentials into Konnect',
      body: (
        <>
          <p className="text-sm text-gray-700 mb-3">
            Open <b>Settings → Enterprise Integrations → Google</b> and paste:
          </p>
          <ul className="text-sm text-gray-800 space-y-1 mb-3 list-disc pl-5">
            <li><b>Client ID</b> — the long string ending in <span className="font-mono">.apps.googleusercontent.com</span></li>
            <li><b>Client Secret</b> — the shorter alphanumeric string</li>
          </ul>
          <Button asChild variant="default" className="gap-2 bg-emerald-600 hover:bg-emerald-700">
            <a href="/settings/integrations">Go to Enterprise Integrations <ChevronRight className="h-3.5 w-3.5" /></a>
          </Button>
          <p className="text-xs text-gray-500 mt-3">
            After saving, come back to Calendar and click <b>Connect Google Calendar</b> — you'll be
            redirected to Google to grant permission, then bounced back automatically.
          </p>
        </>
      ),
    },
  ];

  const stepData = STEPS[step];
  const last = step === STEPS.length - 1;

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="google-setup-guide">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <img src="https://www.gstatic.com/marketing-cms/assets/images/d5/dc/cfe9ce8b4425b410b49b7f2dd3f3/g.webp=s96-fcrop64=1,00000000ffffffff-rw" alt="G" className="h-6 w-6" onError={e => e.target.style.display = 'none'} />
            Connect {info.label}
          </DialogTitle>
        </DialogHeader>

        {/* Step tracker */}
        <div className="flex items-center gap-1.5 mb-1">
          {STEPS.map((s, i) => (
            <div
              key={i}
              className={`flex-1 h-1 rounded-full ${i <= step ? 'bg-emerald-500' : 'bg-gray-200'}`}
            />
          ))}
        </div>
        <div className="text-[11px] text-gray-500 uppercase tracking-wide font-medium">
          Step {step + 1} of {STEPS.length}
        </div>

        <Card className="p-4 mt-1">
          <div className="text-base font-semibold mb-3 flex items-center gap-2">
            <span className="h-6 w-6 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold flex items-center justify-center">
              {step + 1}
            </span>
            {stepData.title}
          </div>
          {stepData.body}
        </Card>

        <div className="flex justify-between items-center pt-2">
          <Button variant="ghost" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}>
            Back
          </Button>
          {last ? (
            <Button onClick={onClose} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="google-setup-done">
              <Check className="h-4 w-4" /> Got it
            </Button>
          ) : (
            <Button onClick={() => setStep(s => Math.min(STEPS.length - 1, s + 1))} className="bg-emerald-600 hover:bg-emerald-700 gap-2">
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
